import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { DragAndDropDirective } from '../../../../directives/drag-and-drop.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { catchError, NEVER, Observable, of, switchMap, tap } from 'rxjs';
import { ShipmentDocumentCategory } from '../../models/documents';
import { AsyncPipe } from '@angular/common';
import { StringHelperService } from '../../../../services/string-helper.service';
import { DocumentsService } from '../../services/documents.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { openConfirmationDialog } from '../../../confirmation-popup/confirmation-popup';
import { GoogleTagManagerService } from '../../../../services/google-tag-manager.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-documents',
  standalone: true,
  templateUrl: './documents.component.html',
  imports: [
    DragAndDropDirective,
    MatTooltip,
    MatButton,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    AsyncPipe
  ],
  styleUrl: './documents.component.scss'
})
export class DocumentsComponent implements OnInit {
  @Input() shipmentNumber?: string | null = null;
  @Input() isArchived = false;
  readonly allowedFormats = '.pdf, .csv, .xls, .xlsx, .docx, .doc, .jpeg, .jpg, .png, .jfif, .gif, .txt, .ppt, .pptx';
  documentName: string | null = null;
  categoryControl = new FormControl(null, Validators.required);
  isUploading = false;
  file: File | null = null;
  categories$: Observable<ShipmentDocumentCategory[]> = of([]);

  private readonly fileMaxSize = 26214400;
  private gtmService = inject(GoogleTagManagerService);
  private documentsService = inject(DocumentsService);
  private stringHelperService = inject(StringHelperService);
  private matSnackbar = inject(MatSnackBar);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.categories$ = this.documentsService.getDocumentCategories();
  }

  handleFileInput(files: FileList | EventTarget | null) {
    if (files) {
      if ((files as HTMLInputElement).files) {
        const inputFiles = (files as HTMLInputElement).files;
        this.file = inputFiles ? inputFiles[0] : null;
      } else {
        this.file = (files as FileList)[0];
      }
      this.documentName = this.file!.name;
    }
  }

  upload() {
    if (this.categoryControl.invalid || !this.documentName) {
      this.categoryControl.markAsTouched();
      return;
    }
    if (this.stringHelperService.notAllowedSpecialChars(this.documentName)) {
      this.matSnackbar.open('File name cannot contain some special characters. Please rename the file.', 'x');
      return;
    }
    if (this.file && this.file.size > this.fileMaxSize) {
      this.matSnackbar.open('Max file size: 25MB', 'x');
      return;
    }

    this.uploadFile();
  }

  clear() {
    this.file = null;
    this.documentName = null;
    this.categoryControl.reset();
  }

  private uploadFile() {
    this.isUploading = true;
    this.documentsService
      .upload({
        file: this.file!,
        shipmentNumber: this.shipmentNumber!,
        category: this.categoryControl.getRawValue()!
      })
      .pipe(
        tap(() => this.handleSuccess('The file was uploaded successfully')),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.gtmService.pushEvent({
      event: 'shipment_details',
      type: { tabs_interacted: 'Document Uploads' }
    });
  }

  private handleSuccess(message: string) {
    this.matSnackbar.open(message, 'x', { panelClass: 'snackbar-green' });
    this.isUploading = false;
    this.clear();
    this.documentsService.refreshDocuments();
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 409) {
      openConfirmationDialog(this.matDialog, {
        title: 'File exists',
        message: 'This file already exist, do you wish to replace it?'
      })
        .afterClosed()
        .pipe(
          switchMap(result => {
            if (result === 'Confirmed') {
              return this.documentsService
                .upload({
                  file: this.file!,
                  category: this.categoryControl.getRawValue()!,
                  shipmentNumber: this.shipmentNumber!,
                  overwrite: true
                })
                .pipe(
                  tap(() => this.handleSuccess('The file was replaced successfully')),
                  catchError(error => this.handleError(error))
                );
            } else {
              return NEVER;
            }
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.matSnackbar.open(error.error.ErrorMessage ?? 'There was an error. Please try again.', 'x');
      console.error(error);
    }
    this.isUploading = false;
    return NEVER;
  }
}
